import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { PageHead, Section } from '../components/ui';
import PriceInput from '../components/PriceInput';

// UX-labeled state options. Backend enum values are kept as-is (STARTED,
// ONGOING, COMPLETE) so existing rows and downstream logic don't need a
// migration; we just render friendlier labels in the picker. PARKED is
// dropped from the picker (still tolerated by gw for legacy rows).
const STATE_OPTIONS = [
  { value: 'STARTED',  label: 'Upcoming' },
  { value: 'ONGOING',  label: 'Ongoing' },
  { value: 'COMPLETE', label: 'Complete' },
];

// Module-level so it isn't redefined on every render (which would remount the
// input and drop focus after each keystroke).
function Field({ label, value, onChange, type = 'text', textarea, required, placeholder }) {
  return (
    <div className="form-field">
      <label>{label}{required && <span className="req"> *</span>}</label>
      {textarea
        ? <textarea className="textarea" value={value} onChange={onChange} placeholder={placeholder} />
        : <input className="input" type={type} value={value} onChange={onChange} placeholder={placeholder} />}
    </div>
  );
}

export default function NewEventPage() {
  const navigate = useNavigate();
  // Events are always public/private toplevel entities now — group-scoping
  // was retired when we removed the Events-in-Groups section. Cross-links
  // to groups happen via the Associations widget on the event editor.
  const [f, setF] = useState({
    requestName: '', requestTopic: '', requestDescription: '',
    date: '', beginTime: '', endTime: '',
    state: 'STARTED',
    location: '', priceCents: 0, isPrivate: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.requestName.trim()) return;
    setBusy(true);
    const trim = (v) => (v && v.trim() ? v.trim() : null);
    try {
      const created = await api.createEvent({
        requestName: f.requestName.trim(),
        requestTopic: trim(f.requestTopic),
        requestDescription: trim(f.requestDescription),
        date: trim(f.date),
        beginTime: trim(f.beginTime),
        endTime: trim(f.endTime),
        state: f.state,
        location: trim(f.location),
        priceCents: Number(f.priceCents) || 0,
        isPrivate: !!f.isPrivate,
      });
      // Route into the manager view rather than the read-only detail page:
      // that's where the Associations widget lives, so the publisher can
      // wire this event to courses / practices / groups right after save.
      navigate(`/leader/event/${created.id}`);
    } catch (err) { alert(err.message); setBusy(false); }
  };

  return (
    <div className="page">
      <PageHead
        breadcrumb={<a onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>Back</a>}
        title="New event"
        sub="Save first, then wire this event to courses, practices, or groups from the editor."
      />
      <form onSubmit={submit} style={{ maxWidth: 640 }}>
        <Section title="Event setup">
          <Field label="Name" required value={f.requestName} onChange={set('requestName')} />
          <Field label="Topic" value={f.requestTopic} onChange={set('requestTopic')} />
          <Field label="Description" textarea value={f.requestDescription} onChange={set('requestDescription')} />
          <Field label="Location" value={f.location} onChange={set('location')} placeholder="address, zoom link, etc." />
          <Field label="Date" type="date" value={f.date} onChange={set('date')} />
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}><Field label="Begin (CST)" type="time" value={f.beginTime} onChange={set('beginTime')} /></div>
            <div style={{ flex: 1 }}><Field label="End (CST)" type="time" value={f.endTime} onChange={set('endTime')} /></div>
          </div>
          <div className="form-field">
            <label>State</label>
            <select className="input" value={f.state} onChange={set('state')}>
              {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Ticket price (USD)</label>
            <PriceInput cents={f.priceCents}
              onChangeCents={(v) => setF((p) => ({ ...p, priceCents: v }))} />
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>0 = free</div>
          </div>
          <div className="form-field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!f.isPrivate}
                onChange={(e) => setF((p) => ({ ...p, isPrivate: e.target.checked }))} />
              <span>Private event</span>
            </label>
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>Hidden from the public catalog. Only the owner and invited participants can see it.</div>
          </div>
          <button className="btn btn-primary" disabled={busy || !f.requestName.trim()}>{busy ? 'Creating…' : 'Create event'}</button>
        </Section>
      </form>
    </div>
  );
}
